import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  Dispatch,
  ReactNode,
} from "react";

// Define user and role types (replace `unknown` with actual types if you know them)
interface User {
  id: string;
  name: string;
  email: string;
}

interface Role {
  role_id: string;
  role_name: string;
}

interface SoftUIState {
  miniSidenav: boolean;
  transparentSidenav: boolean;
  sidenavColor: string;
  transparentNavbar: boolean;
  fixedNavbar: boolean;
  openConfigurator: boolean;
  direction: "ltr" | "rtl";
  layout: string;
  user?: User | null;
  selectedRole?: Role | null;
}

type Action =
  | { type: "MINI_SIDENAV"; value: boolean }
  | { type: "TRANSPARENT_SIDENAV"; value: boolean }
  | { type: "SIDENAV_COLOR"; value: string }
  | { type: "TRANSPARENT_NAVBAR"; value: boolean }
  | { type: "FIXED_NAVBAR"; value: boolean }
  | { type: "OPEN_CONFIGURATOR"; value: boolean }
  | { type: "DIRECTION"; value: "ltr" | "rtl" }
  | { type: "LAYOUT"; value: string }
  | { type: "user/userLogout" };

type SoftUIContextType = [SoftUIState, Dispatch<Action>];

// Create Context
const SoftUI = createContext<SoftUIContextType | null>(null);
SoftUI.displayName = "SoftUIContext";

// Reducer
function reducer(state: SoftUIState, action: Action): SoftUIState {
  switch (action.type) {
    case "MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };
    case "TRANSPARENT_SIDENAV":
      return { ...state, transparentSidenav: action.value };
    case "SIDENAV_COLOR":
      return { ...state, sidenavColor: action.value };
    case "TRANSPARENT_NAVBAR":
      return { ...state, transparentNavbar: action.value };
    case "FIXED_NAVBAR":
      return { ...state, fixedNavbar: action.value };
    case "OPEN_CONFIGURATOR":
      return { ...state, openConfigurator: action.value };
    case "DIRECTION":
      return { ...state, direction: action.value };
    case "LAYOUT":
      return { ...state, layout: action.value };
    case "user/userLogout":
      return { ...state, user: null, selectedRole: null };
    default:
      throw new Error(`Unhandled action type: ${(action as any).type}`);
  }
}

// Provider Props
interface SoftUIControllerProviderProps {
  readonly children: ReactNode;
}

// Provider
export function SoftUIControllerProvider({
  children,
}: SoftUIControllerProviderProps) {
  const initialState: SoftUIState = {
    miniSidenav: false,
    transparentSidenav: true,
    sidenavColor: "secondary",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(
    () => [controller, dispatch] as SoftUIContextType,
    [controller, dispatch]
  );

  return <SoftUI.Provider value={value}>{children}</SoftUI.Provider>;
}

// Hook
export function useSoftUIController(): SoftUIContextType {
  const context = useContext(SoftUI);
  if (!context) {
    throw new Error(
      "useSoftUIController should be used inside the SoftUIControllerProvider."
    );
  }
  return context;
}

// Helper dispatch functions
export const setMiniSidenav = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "MINI_SIDENAV", value });

export const setTransparentSidenav = (
  dispatch: Dispatch<Action>,
  value: boolean
) => dispatch({ type: "TRANSPARENT_SIDENAV", value });

export const setSidenavColor = (dispatch: Dispatch<Action>, value: string) =>
  dispatch({ type: "SIDENAV_COLOR", value });

export const setTransparentNavbar = (
  dispatch: Dispatch<Action>,
  value: boolean
) => dispatch({ type: "TRANSPARENT_NAVBAR", value });

export const setFixedNavbar = (dispatch: Dispatch<Action>, value: boolean) =>
  dispatch({ type: "FIXED_NAVBAR", value });

export const setOpenConfigurator = (
  dispatch: Dispatch<Action>,
  value: boolean
) => dispatch({ type: "OPEN_CONFIGURATOR", value });

export const setDirection = (
  dispatch: Dispatch<Action>,
  value: "ltr" | "rtl"
) => dispatch({ type: "DIRECTION", value });

export const setLayout = (dispatch: Dispatch<Action>, value: string) =>
  dispatch({ type: "LAYOUT", value });
